import { authTokenManager } from "@/services/auth-token-manager";
import { getCustomSentryExceptionError, handleApiErrorWithCode } from "@/utils/error";
import { OAUTH_LOGIN_REASONS } from "@/utils/oauthTelemetry";
import { redirectToLogin } from "@/utils/redirectToLogin";
import { verifyToken } from "@/utils/token";
import { getMatchRoutes, getQueryFromURL } from "@/utils/url";
import { AppRoutes } from "@/routes/config";
import axios, { AxiosError, AxiosInstance, AxiosRequestConfig, AxiosResponse } from "axios";
import Cookies from "js-cookie";
import * as Sentry from "@sentry/react";

if (!process.env.REACT_APP_API_HOST) {
  throw new Error("please provide REACT_APP_API_HOST in env");
}

export const baseURL = `${process.env.REACT_APP_API_HOST}`;

export const globalBaseURL = `${process.env.REACT_APP_API_HOST}/global`;

export const zipCodeURL = `${process.env.REACT_APP_API_ZIP_CODE}`;

export const globalApi = axios.create({
  baseURL: globalBaseURL,
});

export const zipCodeApi = axios.create({
  baseURL: zipCodeURL,
});

export const clientApi = axios.create({
  baseURL,
});

export const onSuccess = (response: AxiosResponse) => {
  Sentry.endSession();
  return response;
};

type TRetryableConfig = AxiosRequestConfig & { _isRetry?: boolean };

// Retry through the *same* axios instance that made the original request so the
// retried call goes back through its request/response interceptors (Sentry
// session, route headers, error handling) instead of bypassing them.
const retryRequestWithToken = (instance: AxiosInstance, config: AxiosRequestConfig, token: string) =>
  instance({
    ...config,
    headers: {
      ...config.headers,
      Authorization: `Bearer ${token}`,
    },
  });

// Created per instance so retries go back through the right one.
export const createOnErrorInterceptor = (instance: AxiosInstance) => async (error: AxiosError) => {
  if (error.response) {
    const query = getQueryFromURL<{ token: string }>();
    const externalToken = query?.token;

    // Shared pages authenticate with a one-off token in the URL: a 401 there
    // means the link is invalid/expired, not that the session expired.
    if (error.response.status === 401 && externalToken) {
      location.href = AppRoutes.pageNotFound;
      return;
    }

    const originalConfig = error.config as TRetryableConfig | undefined;

    // The refresh-token call itself must never be retried through this same
    // logic — otherwise a 401 from /refresh-token (invalid/expired refresh
    // token) gets treated as "needs a token refresh" and deadlocks waiting on
    // the very refresh flow that issued it, so the caller's catch (clear tokens
    // + redirect to login) never runs.
    const isRefreshTokenRequest = originalConfig?.url?.includes("/refresh-token");

    // Guard against infinite loops: a request that already got retried once and
    // still 401s falls through to normal error handling instead of refreshing again.
    if (error.response.status === 401 && originalConfig && !originalConfig._isRetry && !isRefreshTokenRequest) {
      originalConfig._isRetry = true;

      // Read before refreshing — a failed refresh clears the cookies.
      const hadSession = !!Cookies.get("match_token") || !!Cookies.get("refresh_token");

      // A request that raced ahead of setAxiosAuth (no Authorization header)
      // just needs the current cookie token — no refresh needed.
      // `refreshToken()` de-duplicates parallel callers into a single request.
      const newToken = originalConfig.headers?.Authorization ? await authTokenManager.refreshToken() : (Cookies.get("match_token") ?? null);

      if (!newToken) {
        // Anonymous visitors (public share links) have no session to restore —
        // let the caller handle the 401 instead of bouncing them to login.
        if (hadSession) {
          redirectToLogin({ clearToken: true, reason: OAUTH_LOGIN_REASONS.refreshFailedOn401 });
        }
        return Promise.reject(error.response.data);
      }

      setAxiosAuth();
      return retryRequestWithToken(instance, originalConfig, newToken);
    }
  }

  handleApiErrorWithCode(error);
  if (error.response?.status && error.response.status >= 500 && error.response.status !== 503) {
    Sentry.captureException(getCustomSentryExceptionError(error));
  }
  Sentry.endSession();
  return Promise.reject(error.response?.data);
};

clientApi.interceptors.request.use(
  (config) => {
    const matchRoutes = getMatchRoutes();
    config.headers["X-RouteName"] = matchRoutes.at(-1)?.id;

    const screenRouteUrl = new URL(window.location.href);
    screenRouteUrl.search = "";
    config.headers["X-RouteURL"] = screenRouteUrl;

    Sentry.startSession();
    return config;
  },
  (error) => {
    if (error.response?.status && error.response.status >= 500 && error.response.status !== 503) {
      Sentry.captureException(getCustomSentryExceptionError(error));
    }
    Sentry.endSession();
    return Promise.reject(error);
  },
);

globalApi.interceptors.request.use(
  (config) => {
    Sentry.startSession();
    return config;
  },
  (error) => {
    if (error.response?.status && error.response.status >= 500 && error.response.status !== 503) {
      Sentry.captureException(getCustomSentryExceptionError(error));
    }
    Sentry.endSession();
    return Promise.reject(error);
  },
);

clientApi.interceptors.response.use(onSuccess, createOnErrorInterceptor(clientApi));
globalApi.interceptors.response.use(onSuccess, createOnErrorInterceptor(globalApi));

export const setAxiosAuth = () => {
  const token = Cookies.get("match_token");
  clientApi.defaults.headers.common.Authorization = `Bearer ${token}`;
  globalApi.defaults.headers.common.Authorization = `Bearer ${token}`;
};

export const setExternalAxiosAuth = () => {
  const query = getQueryFromURL<{ token: string }>();
  const token = query?.token;
  clientApi.defaults.headers.common.Authorization = `Bearer ${token}`;
  globalApi.defaults.headers.common.Authorization = `Bearer ${token}`;
};

export const removeAxiosAuth = () => {
  clientApi.defaults.headers.common.Authorization = "";
  globalApi.defaults.headers.common.Authorization = "";
};

export const isAuth = () => {
  if (typeof window !== "undefined") {
    const token = Cookies.get("match_token");
    const isAuthenticated = !!token && verifyToken(token);
    return isAuthenticated;
  }
  return false;
};

// Re-exported so existing `import { logout } from "@/utils/api"` call sites keep
// working; the implementation now lives in `@/utils/logout` to avoid an import
// cycle with `redirectToLogin`.
export { logout } from "@/utils/logout";

export const lazyApi = <T = void>(fn: () => Promise<T>): [AbortController, () => Promise<T>] => {
  const controller = new AbortController();

  return [controller, fn];
};
